import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

export default function PopularDestinations({ limit = 8 }) {
    const [destinations, setDestinations] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchDestinations = async () => {
            try {
                const res = await axios.get(
                    "https://dubaihotelrates.com/api/destinations"
                );

                setDestinations(res.data);
            } catch (err) {
                console.error("Failed to load destinations:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchDestinations();
    }, []);

    return (
        <section className="relative overflow-hidden bg-white py-12 sm:py-14 lg:py-16">
            {/* =========================================================
                BACKGROUND DECORATION
            ========================================================== */}
            <div className="pointer-events-none absolute -left-32 top-20 h-72 w-72 rounded-full bg-[#EAF3FF] opacity-60 blur-3xl" />

            <div className="pointer-events-none absolute -right-32 bottom-0 h-72 w-72 rounded-full bg-[#FFF8DD] opacity-50 blur-3xl" />

            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

                {/* =====================================================
                    SECTION HEADER
                ====================================================== */}
                <div className="mb-7 flex items-end justify-between sm:mb-8 lg:mb-9">

                    <div>
                        {/* Small heading */}
                        <div className="mb-2 flex items-center gap-2">
                            <span className="h-[2px] w-6 rounded-full bg-[#FFC928]" />

                            <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#174EA6]">
                                Popular Places
                            </span>
                        </div>

                        {/* Main heading */}
                        <h2 className="text-2xl font-extrabold tracking-tight text-[#0F172A] sm:text-3xl lg:text-[32px]">
                            Top Destinations{" "}
                            <span className="text-[#174EA6]">
                                in Dubai
                            </span>
                        </h2>

                        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                            Explore Dubai's most popular areas and discover
                            great hotels for your next stay.
                        </p>
                    </div>

                    {/* View all */}
                    <button
                        onClick={() => navigate("/destinations")}
                        className="group hidden items-center gap-2 text-sm font-bold text-[#174EA6] transition-all duration-300 sm:flex"
                    >
                        <span>View All Destinations</span>

                        <span className="flex h-7 w-7 items-center justify-center rounded-full border border-[#174EA6]/20 transition-all duration-300 group-hover:translate-x-1 group-hover:bg-[#174EA6] group-hover:text-white">
                            →
                        </span>
                    </button>
                </div>

                {/* =====================================================
                    LOADING STATE
                ====================================================== */}
                {loading ? (
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-6">
                        {[1, 2, 3, 4, 5, 6].map((item) => (
                            <div
                                key={item}
                                className="h-[190px] animate-pulse overflow-hidden rounded-2xl bg-slate-200 sm:h-[210px]"
                            >
                                <div className="h-full w-full bg-gradient-to-b from-slate-100 to-slate-300" />
                            </div>
                        ))}
                    </div>
                ) : (
                    <>
                        {/* =================================================
                            DESTINATION CARDS
                        ================================================== */}
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-6">

                            {destinations
                                .slice(0, limit)
                                .map((dest, index) => (
                                    <div
                                        key={dest.id}
                                        onClick={() =>
                                            navigate(
                                                `/destinations/${dest.slug}`
                                            )
                                        }
                                        className="group relative h-[190px] cursor-pointer overflow-hidden rounded-2xl bg-slate-900 shadow-[0_6px_20px_rgba(15,23,42,0.10)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_16px_35px_rgba(15,23,42,0.18)] sm:h-[210px]"
                                        style={{
                                            animation: `destinationFadeUp 0.55s ease-out ${index * 80
                                                }ms both`,
                                        }}
                                    >
                                        {/* =================================================
                                            IMAGE
                                        ================================================== */}
                                        <img
                                            src={`https://uaediscountedhotels.com/uploads/destinations/${dest.image}`}
                                            alt={dest.name}
                                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                                        />

                                        {/* =================================================
                                            IMAGE OVERLAY
                                        ================================================== */}
                                        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-black/10 to-[#001925]/90" />

                                        {/* Hover overlay */}
                                        <div className="absolute inset-0 bg-[#174EA6]/0 transition-all duration-500 group-hover:bg-[#174EA6]/20" />

                                        {/* =================================================
                                            TOP NUMBER
                                        ================================================== */}
                                        <div className="absolute left-3 top-3">
                                            <div className="flex h-8 w-8 items-center justify-center rounded-full border border-white/40 bg-white/90 shadow-lg backdrop-blur-md">
                                                <span className="text-[10px] font-extrabold text-[#174EA6]">
                                                    {String(index + 1).padStart(
                                                        2,
                                                        "0"
                                                    )}
                                                </span>
                                            </div>
                                        </div>

                                        {/* =================================================
                                            HOTEL COUNT
                                        ================================================== */}
                                        <div className="absolute right-3 top-3">
                                            <div className="flex h-7 min-w-[72px] items-center justify-center rounded-full border border-white/20 bg-[#002B45]/80 px-3 shadow-lg backdrop-blur-md">
                                                <span className="text-center text-[9px] font-semibold leading-none text-white">
                                                    {dest.hotels_count} Hotels
                                                </span>
                                            </div>
                                        </div>

                                        {/* =================================================
                                            BOTTOM CONTENT
                                        ================================================== */}
                                        <div className="absolute bottom-0 left-0 right-0 p-3">

                                            {/* Location icon */}
                                            <div className="mb-1 flex items-center gap-1.5">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    fill="currentColor"
                                                    className="h-3.5 w-3.5 text-white"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M11.54 22.351l.07.04.028.016a.76.76 0 00.723 0l.028-.015.071-.041a16.64 16.64 0 001.42-.969c.879-.683 1.88-1.647 2.864-2.76 1.933-2.188 3.79-5.083 3.79-8.122a8.535 8.535 0 00-17.07 0c0 3.04 1.857 5.934 3.79 8.122a30.5 30.5 0 004.284 3.729zM12 13.5a3 3 0 100-6 3 3 0 000 6z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>

                                                <span className="text-[9px] font-medium uppercase tracking-wider text-white/80">
                                                    Dubai
                                                </span>
                                            </div>

                                            {/* Destination name */}
                                            <div className="flex items-end justify-between gap-2">
                                                <div className="min-w-0">
                                                    <h3 className="truncate text-sm font-extrabold text-white sm:text-[15px]">
                                                        {dest.name}
                                                    </h3>

                                                    <p className="mt-0.5 text-[9px] text-white/75">
                                                        {dest.hotels_count}+
                                                        Hotels
                                                    </p>
                                                </div>

                                                {/* Arrow */}
                                                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-md transition-all duration-300 group-hover:translate-x-1 group-hover:bg-[#FFC928] group-hover:text-[#174EA6]">
                                                    <span className="text-sm">
                                                        →
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* =================================================
                                            GOLD HOVER LINE
                                        ================================================== */}
                                        <div className="absolute bottom-0 left-0 h-1 w-0 bg-[#FFC928] transition-all duration-500 group-hover:w-full" />
                                    </div>
                                ))}
                        </div>

                        {/* =====================================================
                            MOBILE VIEW ALL
                        ====================================================== */}
                        <div className="mt-6 sm:hidden">
                            <button
                                onClick={() => navigate("/destinations")}
                                className="group flex w-full items-center justify-center gap-2 rounded-xl bg-[#174EA6] px-5 py-3 font-semibold text-white shadow-[0_8px_20px_rgba(23,78,166,0.18)] transition-all duration-300 hover:bg-[#123D85]"
                            >
                                <span>View All Destinations</span>

                                <span className="text-[#FFC928] transition-transform duration-300 group-hover:translate-x-1">
                                    →
                                </span>
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* =============================================================
                CARD ANIMATION
            ============================================================== */}
            <style>
                {`
                    @keyframes destinationFadeUp {
                        from {
                            opacity: 0;
                            transform: translateY(15px);
                        }

                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                `}
            </style>
        </section>
    );
}